import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleContent } from '@/mocks/article';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const movieArticle = id && articleContent[id];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        {/* Breadcrumb */}
        <nav aria-label="面包屑导航" className="bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 py-4">
            <ol className="flex items-center gap-2 text-xs text-foreground-600 flex-wrap">
              <li><Link to="/" className="hover:text-primary-500 transition-colors">首页</Link></li>
              <li className="text-foreground-400">/</li>
              <li><Link to="/category/all" className="hover:text-primary-500 transition-colors">影视资讯</Link></li>
              {movieArticle && (
                <>
                  <li className="text-foreground-400">/</li>
                  <li className="text-foreground-900 font-medium line-clamp-1">{movieArticle.title}</li>
                </>
              )}
            </ol>
          </div>
        </nav>

        {movieArticle ? (
          <article className="py-10 md:py-14 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <header className="mb-8">
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <span className="text-xs bg-accent-100 text-accent-700 px-2.5 py-1 rounded-full font-medium">
                      {movieArticle.category}
                    </span>
                    <span className="text-xs text-foreground-500">{movieArticle.date}</span>
                    <span className="text-xs text-foreground-500">|</span>
                    <span className="text-xs text-foreground-500">
                      <span className="w-3 h-3 inline-flex items-center justify-center mr-0.5">
                        <i className="ri-eye-line text-xs"></i>
                      </span>
                      {movieArticle.readCount} 阅读
                    </span>
                  </div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 leading-snug mb-4">
                    {movieArticle.title}
                  </h1>
                  <p className="text-sm text-foreground-500">作者：{movieArticle.author}</p>
                </header>

                <div className="aspect-[2/1] rounded-lg overflow-hidden bg-background-200 mb-8">
                  <img
                    src={movieArticle.image}
                    alt={movieArticle.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="prose-content">
                  {movieArticle.content.map((block, index) => {
                    if (block.type === 'heading' && block.text) {
                      return (
                        <h2 key={index} className="text-xl font-heading font-bold text-foreground-900 mt-8 mb-4 pb-2 border-b border-background-200/70">
                          {block.text}
                        </h2>
                      );
                    }
                    if (block.type === 'paragraph' && block.text) {
                      return (
                        <p key={index} className="text-sm text-foreground-700 leading-relaxed mb-4">
                          {block.text}
                        </p>
                      );
                    }
                    return null;
                  })}
                </div>

                <div className="mt-10 pt-6 border-t border-background-200/70">
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="text-xs text-foreground-500">标签：</span>
                    <span className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full">片多多影视</span>
                    <span className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full">{movieArticle.category}</span>
                    <span className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full">影视资讯</span>
                  </div>
                </div>

                <nav className="mt-6 flex items-center justify-between">
                  <Link
                    to="/category/all"
                    className="flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-line"></i>
                    </span>
                    返回列表
                  </Link>
                </nav>
              </div>
            </div>
          </article>
        ) : (
          <section className="py-20 bg-background-50 text-center">
            <div className="w-full px-4 md:px-6">
              <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-400">
                <i className="ri-article-line text-4xl"></i>
              </span>
              <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
              <p className="text-sm text-foreground-600 mb-6">该文章不存在或已被删除</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-all whitespace-nowrap cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          </section>
        )}
      </main>
      <Footer />
      {movieArticle && (
        <>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                  {
                    '@type': 'ListItem',
                    position: 1,
                    name: '首页',
                    item: 'https://pianduoduo.com',
                  },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: '影视资讯',
                    item: 'https://pianduoduo.com/category/all',
                  },
                  {
                    '@type': 'ListItem',
                    position: 3,
                    name: movieArticle.title,
                  },
                ],
              }),
            }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: movieArticle.title,
                author: {
                  '@type': 'Person',
                  name: movieArticle.author,
                },
                datePublished: movieArticle.date,
                image: movieArticle.image,
                publisher: {
                  '@type': 'Organization',
                  name: '片多多影视',
                },
                description: movieArticle.content.find((c) => c.type === 'paragraph')?.text || '',
              }),
            }}
          />
        </>
      )}
    </>
  );
}